import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, Observable, timeout } from 'rxjs';
import { LoadingIndicatorService } from '#services';
import { DEFAULT_TOKEN_WAIT_TIMEOUT_MS } from '../http-timeout.token';

/**
 * Global HTTP concern for every `/api` call:
 *  - caps the wait for the first event (the backend's Sent event, emitted only
 *    after MsalInterceptor has the token) so a hung token acquisition can never
 *    hang the UI forever; HttpResilienceInterceptor bounds each attempt after that;
 *  - tracks the request in the LoadingIndicatorService and always releases it
 *    via `finalize`, so the global spinner can never get stuck "on".
 *
 * Registered BEFORE the MsalInterceptor so its first-event cap covers token acquisition.
 */
@Injectable()
export class HttpLoadingInterceptor implements HttpInterceptor {
  private _loadingIndicator = inject(LoadingIndicatorService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Non-`/api` requests (e.g. i18n/asset loads) pass through untouched — no
    // timeout and no spinner tracking.
    if (!req.url.includes('/api')) {
      return next.handle(req);
    }

    this._loadingIndicator.increment();

    return next.handle(req).pipe(
      timeout({ first: DEFAULT_TOKEN_WAIT_TIMEOUT_MS }),
      finalize(() => this._loadingIndicator.decrement())
    );
  }
}
